import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { IDigitalProductBillingType } from '../../digitalProduct/models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../digitalProduct/models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../digitalProduct/models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../digitalProduct/models/digitalProduct.model';

type SubscriptionUpsertPayload = {
    name: string;
    description?: string | null;
    coverUrl?: string | null;
    currency: string;
    recurringInterval: IDigitalProductRecurringInterval;
    recurringIntervalCount?: number | null;
    trialDays?: number | null;
    recurringFee?: number | null;
    visibility: IDigitalProductVisibility;
};

@Injectable()
export class SubscriptionService {
    private http = inject(HttpClient);
    authService = inject(AuthenticationService);
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };

    getSubscriptions(): Observable<IDigitalProduct[]> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (this.hasValidToken(token)) {
            return this.http.get<IDigitalProduct[]>(`${environment.API_URL}/api/Subscription/mine`, options).pipe(
                map((subscriptions) => subscriptions.map((subscription) => this.normalizeSubscription(subscription)))
            );
        }

        return of();
    }

    getSubscription(id: string | undefined): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!id || !this.hasValidToken(token)) {
            return of();
        }

        return this.http.get<IDigitalProduct>(`${environment.API_URL}/api/Subscription/public/${id}`, options).pipe(
            map((subscription) => this.normalizeSubscription(subscription))
        );
    }

    getPublicSubscriptionByUserAndSlug(userId: string | undefined, slug: string | undefined): Observable<IDigitalProduct | null> {
        const normalizedUserId = userId?.trim().replace(/^@/, '');
        const normalizedSlug = slug?.trim();

        if (!normalizedUserId || !normalizedSlug) {
            return of(null);
        }

        const encodedUserId = encodeURIComponent(normalizedUserId);
        const encodedSlug = encodeURIComponent(normalizedSlug);
        const pathEndpoint = `${environment.API_URL}/api/Subscription/public/${encodedUserId}/${encodedSlug}`;

        return this.http.get<IDigitalProduct>(pathEndpoint).pipe(
            map((subscription) => this.normalizeSubscription(subscription)),
            catchError(() => of(null))
        );
    }

    createSubscription(subscription: IDigitalProduct): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token)) {
            return of();
        }

        const payload = this.sanitizeSubscription(subscription);
        return this.http.post<IDigitalProduct>(`${environment.API_URL}/api/Subscription/create`, payload, options).pipe(
            map((savedSubscription) => this.normalizeSubscription(savedSubscription))
        );
    }

    editSubscription(subscription: IDigitalProduct): Observable<IDigitalProduct> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token) || !subscription.id) {
            return of();
        }

        const payload = this.sanitizeSubscription(subscription);
        return this.http.put<IDigitalProduct>(`${environment.API_URL}/api/Subscription/${subscription.id}`, payload, options).pipe(
            map((updatedSubscription) => this.normalizeSubscription(updatedSubscription))
        );
    }

    deleteSubscription(id: string): Observable<void> {
        const token = this.authService.getUserToken();
        const options = this.getAuthOptions(token);

        if (!this.hasValidToken(token)) {
            return of();
        }

        return this.http.delete<void>(`${environment.API_URL}/api/Subscription/${id}`, options);
    }

    changeCover(imageData: string | File | null, subscriptionId: string): Observable<string> {
        const token = this.authService.getUserToken();

        if (!this.hasValidToken(token)) {
            return of();
        }

        const formData = new FormData();
        formData.append('subscriptionId', subscriptionId);
        if (imageData instanceof File) {
            formData.append('imageFile', imageData, imageData.name);
        }

        const options = {
            headers: new HttpHeaders({
                Authorization: `Bearer ${token}`
            }),
            contentType: 'multipart/form-data',
            body: formData
        };

        return this.http.post<string>(`${environment.API_URL}/api/Subscription/updateCover`, formData, options);
    }

    private getAuthOptions(token: string | null): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-type': 'application/json',
                Authorization: `Bearer ${token}`
            })
        };
    }

    private hasValidToken(token: string | null): token is string {
        return token !== undefined && token !== null && token !== '';
    }

    private sanitizeSubscription(subscription: IDigitalProduct): SubscriptionUpsertPayload {
        const recurringInterval = this.resolveRecurringInterval(subscription.recurringInterval);
        const visibility = this.resolveVisibility(subscription.visibility);

        return {
            name: (subscription.name ?? '').trim(),
            description: subscription.description?.trim() ?? null,
            coverUrl: subscription.coverUrl?.trim() ?? null,
            currency: (subscription.currency ?? 'USD').trim().toUpperCase(),
            recurringInterval: recurringInterval === IDigitalProductRecurringInterval.None
                ? IDigitalProductRecurringInterval.Month
                : recurringInterval,
            recurringIntervalCount: subscription.recurringIntervalCount ?? 1,
            trialDays: subscription.trialDays ?? null,
            recurringFee: subscription.recurringFee ?? null,
            visibility
        };
    }

    private normalizeSubscription(subscription: IDigitalProduct): IDigitalProduct {
        return {
            ...subscription,
            billingType: IDigitalProductBillingType.Recurring,
            recurringInterval: this.resolveRecurringInterval(subscription.recurringInterval),
            oneTimeFee: null,
            visibility: this.resolveVisibility(subscription.visibility)
        };
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval {
        return this.resolveEnumValue(value, this.recurringIntervalMap) ?? IDigitalProductRecurringInterval.Month;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility {
        return this.resolveEnumValue(value, this.visibilityMap) ?? IDigitalProductVisibility.Draft;
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        return map[String(value).trim()];
    }
}
